// @ts-strict-ignore
import isEmpty from 'lodash/isEmpty';

import {Translatable} from 'common/i18n';
import {BookmarkOverrides, getDashboardParamsSafe} from 'common/report/util/bookmark-overrides';
import {ChartVizType, ProjectFeatureFlag, ResourceType} from 'common/report/constants';
import {encodeRison, serializeJsUrl} from 'common/report/util/url';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {getReportUrl} from 'common/report/util';
import {getInvalidChartTypesForInsightsBookmark} from 'common/report/insights/util';
import {COMMON_CHART_TYPE_TO_API_CHART_TYPE} from 'common/report/retention/util/constants';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {isAnyOrderFunnel} from 'common/report/funnels/util';
import {
  Bookmark,
  CardOnDashboard,
  Dashboard,
  DashboardCard,
  DashboardCardChartOptions,
  DataGroupNotSupportedReason,
  DataGroupNotSupportedReasonCode,
  TimeFilter,
} from 'common/report/dashboards/types';
import {ChartType as FunnelChartType} from 'common/report/funnels/models/types';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {ComparisonDateRangeBookmarkFormat} from 'common/query-builder/query-block/time-comparison-menu/types';
import {
  comparisonDateFromBookmarkFormat,
  getTimeComparisonRangeText,
} from 'common/query-builder/query-block/time-comparison-menu/utils';
import {Segmentation} from 'common/types/reports/query';
import {
  getBehaviorShowClauses,
  hasAnyOrderFunnel,
  hasSavedFormula,
  isBehaviorShowClause,
} from 'common/report/insights/models/utils';
import {DisplayOptions, isInsightsBookmarkParams} from 'common/report/insights/util/types';
import {ChartPlotStyle, ChartType, InsightsBookmarkParams} from 'common/types/reports/bookmark';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import {CurrentProject} from 'common/report/model-resources/projects';
import type {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {isGroupPropertyFilter} from 'common/widgets/property-filter-menu/models/utils';

import {RETENTION_TYPE_TO_NAME} from '../models/constants';

export function checkBookmarkFilterOverridesSupported(
  bookmark: any,
  filters: Segmentation[],
): {dataGroupNotSupported: boolean; dataGroupNotSupportedReason?: DataGroupNotSupportedReason} {
  if (filters.length > 0) {
    const {params, original_type: originalType} = bookmark;

    let dataGroupId = null;
    switch (originalType) {
      case BookmarkType.Insights: {
        if (params.sections) {
          dataGroupId = getBehaviorShowClauses(params.sections.show)[0]?.behavior.dataGroupId;
        }
        break;
      }
      case BookmarkType.Funnels:
      case BookmarkType.Retention: {
        dataGroupId = params.data_group_id;
        break;
      }
    }

    // user filters are not supported when analyzing in a data group different than user
    const hasUserFilters = filters.some((filter) => filter.resourceType === ResourceType.User && !filter.dataGroupId);
    if (dataGroupId && hasUserFilters) {
      return {
        dataGroupNotSupported: true,
        dataGroupNotSupportedReason: DataGroupNotSupportedReasonCode.UserFilterOnDataGroup,
      };
    }

    const hasMismatchingDataGroupId = filters.some(
      (filter) =>
        filter.resourceType === ResourceType.User && Boolean(filter.dataGroupId) && dataGroupId !== filter.dataGroupId,
    );
    if (dataGroupId && hasMismatchingDataGroupId) {
      return {
        dataGroupNotSupported: true,
        dataGroupNotSupportedReason: DataGroupNotSupportedReasonCode.DataGroupIdMisMatch,
      };
    }
  }

  return {dataGroupNotSupported: false};
}

// TODO opslead
export function getBookmarkUrlPath(
  bookmark: Bookmark,
  {
    bookmarkMetadata = {},
    chartType,
    originDashboard,
    filters,
    breakdowns,
    timeFilter,
  }: {
    bookmarkMetadata?: any;
    chartType?: ChartType;
    originDashboard?: Dashboard;
    filters?: PropertyFilter[];
    breakdowns?: Segmentation[];
    timeFilter?: TimeFilter;
  },
) {
  const commonOverrides: BookmarkOverrides = {
    ...(isEmpty(filters) ? {} : {additionalFilters: filters.map((f) => f.getAttributes())}),
    ...(isEmpty(breakdowns) ? {} : {additionalBreakdowns: breakdowns}),
    ...(isEmpty(timeFilter) ? {} : {dateRangeOverride: timeFilter.dateRange.getAttributes()}),
    ...(originDashboard ? {originDashboard: getDashboardParamsSafe(originDashboard, {urlEncode: true})} : {}),
  };
  // bookmark will be insight shape if report_consistency=1 is turned on
  const type = isInsightsBookmarkParams(bookmark.params) ? BookmarkType.Insights : bookmark.type;

  switch (type) {
    case BookmarkType.Insights: {
      const reportPath = bookmark.original_type === `segmentation3` ? `segmentation-report` : `report`;
      const displayOptions: DisplayOptions = bookmark.params.displayOptions;
      const overrides: BookmarkOverrides = {
        ...commonOverrides,
        ...(chartType && chartType !== displayOptions.chartType ? {chartTypeOverride: chartType} : {}),
      };
      const overridesString = isEmpty(overrides) ? `` : `/${serializeJsUrl(overrides)}`;
      return `${getReportUrl(`${bookmark.type}`, {
        projectId: bookmark.project_id,
        workspaceId: bookmark.workspace_id,
      })}#${reportPath}/${bookmark.id}${overridesString}`;
    }
    case BookmarkType.Flows: {
      const overrides: BookmarkOverrides = {
        ...commonOverrides,
        ...(chartType && chartType !== bookmark.params.chartType ? {chartTypeOverride: chartType} : {}),
      } as BookmarkOverrides; // TODO: Fix types to remove this cast;

      const overridesString = isEmpty(overrides) ? `` : `/${serializeJsUrl(overrides)}`;
      return `${getReportUrl(`flows`, {
        projectId: bookmark.project_id,
        workspaceId: bookmark.workspace_id,
      })}#report/${bookmark.id}${overridesString}`;
    }
    // TODO: remove all funnel & retention logic once report_consistency goes to 100%
    case BookmarkType.Funnels: {
      const isFunnelChartTypeSupported = [
        ChartType.FunnelFrequencyBar,
        ChartType.FunnelFrequencyLine,
        ChartType.FunnelMedianTTC,
        ChartType.FunnelSteps,
        ChartType.FunnelTrend,
        ChartType.FunnelTTCBar,
        ChartType.FunnelTTCLine,
        ChartType.FunnelStepsMetric,
      ].includes(chartType); // TODO: Fix types to remove this cast

      const overrides: BookmarkOverrides = {
        ...commonOverrides,
        ...(isFunnelChartTypeSupported ? {chartTypeOverride: chartType} : {}),
      } as BookmarkOverrides;

      let overrideSeparator = `/`;
      // if url_hash already contains overriding params, append additional overrides with `,`, otherwise use `/`
      if (bookmarkMetadata.url_hash?.match(/view\/(\d+)\/(.+)/)) {
        overrideSeparator = `,`;
      }
      const overridesString = isEmpty(overrides) ? `` : `${overrideSeparator}${encodeRison(overrides)}`;
      return `${getReportUrl(`funnels`, {
        projectId: bookmark.project_id,
        workspaceId: bookmark.workspace_id,
      })}#${bookmarkMetadata.url_hash || ``}${overridesString}`;
    }
    case BookmarkType.Retention: {
      // dashboard retention chart types do not seem to match retention report chart types at all
      const hasChartTypeOverride =
        chartType && bookmark.params.chart_type !== COMMON_CHART_TYPE_TO_API_CHART_TYPE[chartType];

      const overrides: BookmarkOverrides = {
        ...commonOverrides,
        ...(hasChartTypeOverride ? {chartTypeOverride: chartType} : {}),
      } as BookmarkOverrides; // TODO: Fix types to remove this cast
      const overridesString = isEmpty(overrides) ? `` : `/${encodeRison(overrides)}`;
      return `${getReportUrl(`retention`, {
        projectId: bookmark.project_id,
        workspaceId: bookmark.workspace_id,
      })}#${bookmarkMetadata.url_hash || ``}${overridesString}`;
    }
    default: {
      const hash = bookmark[`url_hash`] || `#${encodeRison(bookmark.params)}`;
      return `${getReportUrl(bookmark.type, {
        projectId: bookmark.project_id,
        workspaceId: bookmark.workspace_id,
      })}${hash}`;
    }
  }
}

//chart types that are allowed in dashboard but are not valid for a given bookmark
export function getInvalidChartTypesForBookmark({
  bookmark,
  metricsResource,
  behaviorsResource,
}: {
  bookmark: any;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
  currentProject: CurrentProject;
}): Array<{type: ChartType; reason?: Translatable}> {
  const isMultiMetric = isMultiMetricBookmark(bookmark.params);

  switch (bookmark.type) {
    case BookmarkType.Funnels: {
      const isAnyOrder = isMultiMetric
        ? hasAnyOrderFunnel(bookmark.params.sections.show)
        : isAnyOrderFunnel(bookmark.params.funnel_order);
      if (!isAnyOrder) {
        return [
          {
            type: ChartType.FunnelTopPaths,
            reason: ({t}) => t(`reports.chart-types.disabled-reasons.top-paths-funnels`),
          },
        ];
      }
      break;
    }
    case BookmarkType.Insights: {
      const invalidChartTypes = getInvalidChartTypesForInsightsBookmark({
        bookmarkParams: bookmark.params,
        metricsResource,
        behaviorsResource,
      });
      return invalidChartTypes;
    }
  }
  return [];
}

export function isMultiMetricBookmark(bookmark: InsightsBookmarkParams): boolean {
  const showArray = bookmark.sections?.show;
  return showArray && (showArray.some((show) => isBehaviorShowClause(show)) || hasSavedFormula(bookmark));
}

// WHEN CHANGING THIS LOGIC KEEP THE LOGIC IN SYNC WITH perf/dashboard_cache_warmer/query/reports/
export function getSupportedChartTypes({
  bookmark,
  metricsResource,
  behaviorsResource,
  currentProject,
}: {
  bookmark: any;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
  currentProject: CurrentProject;
}): ChartType[] {
  let chartTypes = [];
  if (isMultiMetricBookmark(bookmark.params)) {
    switch (bookmark.type) {
      case BookmarkType.Funnels: {
        chartTypes = [ChartType.FunnelSteps, ChartType.Line, ChartType.Bar, ChartType.Metric, ChartType.FunnelTopPaths];
        break;
      }
      case BookmarkType.Insights: {
        chartTypes = [
          ChartType.Bar,
          ChartType.StackedBar,
          ChartType.Line,
          ChartType.StackedLine,
          ChartType.Column,
          ChartType.StackedColumn,
          ChartType.Metric,
          ChartType.Pie,
          ChartType.Table,
          ChartType.FunnelSteps,
          ChartType.RetentionCurve,
        ];
        break;
      }
      case BookmarkType.Retention: {
        chartTypes = [ChartType.RetentionCurve, ChartType.Line, ChartType.Metric];
        break;
      }
    }
  } else {
    switch (bookmark.type) {
      case BookmarkType.Funnels: {
        chartTypes = [
          ChartType.FunnelSteps,
          ChartType.FunnelTrend,
          ChartType.FunnelStepsMetric,
          ChartType.FunnelTTCBar,
          ChartType.FunnelTTCLine,
          ChartType.FunnelMedianTTC,
          ChartType.FunnelFrequencyBar,
          ChartType.FunnelFrequencyLine,
          ChartType.FunnelTopPaths,
        ];
        break;
      }
      case BookmarkType.Insights: {
        chartTypes = [
          ChartType.Bar,
          ChartType.StackedBar,
          ChartType.Line,
          ChartType.StackedLine,
          ChartType.Metric,
          ChartType.Pie,
          ChartType.Table,
        ];
        break;
      }
      case BookmarkType.Retention: {
        chartTypes = [ChartType.RetentionLine, ChartType.RetentionTrend, ChartType.RetentionTrendMetric];
        break;
      }
      case BookmarkType.Flows: {
        chartTypes = [ChartType.Sankey, ChartType.FlowsTopPaths];
        break;
      }
    }
  }
  const invalidChartTypesForBookmark = getInvalidChartTypesForBookmark({
    bookmark,
    metricsResource,
    behaviorsResource,
    currentProject,
  });
  return chartTypes.filter((chartType) => !invalidChartTypesForBookmark.some((ch) => ch.type === chartType));
}

// Keep in sync with perf/dashboard_cache_warmer/query/reports/query_params.py::get_chart_options_from_bookmark
export function getBookmarkChartOptions(bookmark: any): null | DashboardCardChartOptions {
  if (bookmark.type === BookmarkType.Insights) {
    // This assignment is only for type checking until `bookmark.params` gets typed properly.
    const displayOptions: DisplayOptions = bookmark.params.displayOptions;
    switch (displayOptions.chartType) {
      case ChartType.Bar: {
        const isStacked = displayOptions.plotStyle === ChartPlotStyle.Stacked;
        return isStacked ? {type: ChartType.StackedBar} : {type: ChartType.Bar};
      }
      case ChartType.Line: {
        const isStacked = displayOptions.plotStyle === ChartPlotStyle.Stacked;
        return isStacked ? {type: ChartType.StackedLine} : {type: ChartType.Line};
      }
      case ChartType.Column: {
        const isStacked = displayOptions.plotStyle === ChartPlotStyle.Stacked;
        return isStacked ? {type: ChartType.StackedColumn} : {type: ChartType.Column};
      }
      default:
        return {type: bookmark.params.displayOptions.chartType};
    }
  } else if (bookmark.type === BookmarkType.Retention) {
    if (isMultiMetricBookmark(bookmark.params)) {
      return getBookmarkChartOptions({...bookmark, type: BookmarkType.Insights});
    }
    switch (bookmark.params.chart_type) {
      case ChartType.RetentionTrend:
        return {type: ChartType.RetentionTrend};
      case ChartType.RetentionTrendMetric:
        return {type: ChartType.RetentionTrendMetric};
      default:
        return {type: ChartType.RetentionLine};
    }
  } else if (bookmark.type === BookmarkType.Flows) {
    if (bookmark.params.chartType === ChartType.Sankey) {
      return {type: ChartType.Sankey};
    } else if (bookmark.params.chartType === ChartType.FlowsTopPaths) {
      return {type: ChartType.FlowsTopPaths};
    }
  } else if (bookmark.type === BookmarkType.Funnels) {
    if (isMultiMetricBookmark(bookmark.params)) {
      return getBookmarkChartOptions({...bookmark, type: BookmarkType.Insights});
    }
    switch (bookmark.params.chart_type) {
      case FunnelChartType.TimeToConvert: {
        if (bookmark.params.ttcVizType === ChartVizType.Bar) {
          return {type: ChartType.FunnelTTCBar};
        } else if (bookmark.params.ttcVizType === ChartVizType.Metric) {
          return {type: ChartType.FunnelMedianTTC};
        }

        return {type: ChartType.FunnelTTCLine};
      }
      case FunnelChartType.Frequency: {
        if (bookmark.params.frequencyVizType === ChartVizType.Bar) {
          return {type: ChartType.FunnelFrequencyBar};
        }
        return {type: ChartType.FunnelFrequencyLine};
      }
      case FunnelChartType.Trends: {
        return {type: ChartType.FunnelTrend};
      }
      case FunnelChartType.Sankey: {
        return {type: ChartType.FunnelTopPaths};
      }
      case FunnelChartType.Chart: {
        if (bookmark.params.stepsVizType === ChartVizType.Metric) {
          return {type: ChartType.FunnelStepsMetric};
        }

        return {type: ChartType.FunnelSteps};
      }
    }
  }
  return null;
}

export function getNewMetricChartType(bookmarkType: BookmarkType) {
  switch (bookmarkType) {
    case BookmarkType.Insights:
      return ChartType.Metric;
    case BookmarkType.Funnels:
      return ChartType.FunnelStepsMetric;
    case BookmarkType.Retention:
      return ChartType.RetentionTrendMetric;
  }

  return null;
}

export function getChartOptions({
  bookmark,
  chartOptionsOverride,
  metricsResource,
  behaviorsResource,
  currentProject,
}: {
  bookmark: any;
  chartOptionsOverride: null | DashboardCardChartOptions;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
  currentProject: CurrentProject;
}): DashboardCardChartOptions {
  let chartOptions = chartOptionsOverride;

  if (chartOptions?.type === ChartType.LegacyMetric) {
    const type = getNewMetricChartType(bookmark.type);
    chartOptions = type ? {type} : null;
  }

  if (isEmpty(chartOptions)) {
    chartOptions = getBookmarkChartOptions(bookmark);
  }

  // special handling for public dashboard
  if (metricsResource && behaviorsResource) {
    const supportedChartTypes = getSupportedChartTypes({bookmark, metricsResource, behaviorsResource, currentProject});
    if (!chartOptions || !supportedChartTypes.includes(chartOptions.type)) {
      // return default chart type
      return {type: supportedChartTypes[0]};
    }
  }

  return chartOptions;
}

// as of writing, only Insights bookmarks have dataset names, but this method is in common report utils because
// dataset names are currently used to support rollup projects, which will (in theory) eventually apply to all reports
// note: "dataset_name" may no longer be used for rollup projects soon and this method should be updated to use the new rollup property: https://mixpanel.atlassian.net/browse/DG-743
export function getDatasetName(bookmark: any): null | string {
  if (bookmark.type === BookmarkType.Insights && bookmark.params.sections.show) {
    const datasetName = getBehaviorShowClauses(bookmark.params.sections.show)
      .map((clause) => clause.behavior.dataset)
      .filter(Boolean)[0];
    if (datasetName !== `$mixpanel`) {
      return datasetName;
    }
  }
  return null;
}

export function getCardTrackingParams({
  card,
  metricsResource,
  behaviorsResource,
  currentProject,
}: {
  card: CardOnDashboard;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
  currentProject: CurrentProject;
}) {
  const dashboardCard = card as DashboardCard;
  return {
    'report info: card id': card.id,
    'card type': card.type,
    ...(dashboardCard.bookmark
      ? {
          'report info: chart type': getChartOptions({
            bookmark: dashboardCard.bookmark,
            chartOptionsOverride: dashboardCard.chart_options,
            metricsResource,
            behaviorsResource,
            currentProject,
          }).type,
        }
      : {}),
    'can override legend colors': Boolean(
      dashboardCard.bookmark && currentProject?.hasFeatureFlag(ProjectFeatureFlag.ConsistentColors),
    ),
  };
}

export function getBookmarkTrackingParams(bookmark: any, bookmarkMetadata: any = {}) {
  let reportExtra = {};
  switch (bookmark.type) {
    case BookmarkType.Funnels: {
      reportExtra = {
        'report info: # of days queried': bookmarkMetadata.num_days,
        'report info: segmented funnel': bookmarkMetadata.segmented,
      };
      break;
    }
    case BookmarkType.Insights: {
      reportExtra = {
        'dataset name': getDatasetName(bookmark) || `mixpanel`,
      };
      break;
    }
    case BookmarkType.Retention: {
      if (!isMultiMetricBookmark(bookmark.params)) {
        const dateRange = bookmark.params.date_range;
        const dateRangeNumDays =
          `type` in dateRange
            ? DateRange.fromBookmarkFormat(dateRange).getIntervalDays()
            : dateRange.to - dateRange.from;
        reportExtra = {
          'retention type': RETENTION_TYPE_TO_NAME[bookmark.params.retention_type].toLowerCase(),
          'report info: # of days queried': dateRangeNumDays,
        };
      }
      break;
    }
  }

  return {
    project_id: bookmark.project_id,
    'report info: name': bookmark.name,
    'report info: bookmark id': bookmark.id,
    'report info: project id': bookmark.project_id,
    'report info: workspace id': bookmark.workspace_id,
    'report info: type': bookmark.type,
    'report info: # of days queried':
      bookmarkMetadata && bookmarkMetadata.num_days
        ? bookmarkMetadata.num_days
        : bookmark.params.to_date - bookmark.params.from_date,
    'report info: is default': bookmark.is_default,
    'report info: legend color overrides': Object.keys(bookmark.params.legend?.overrides ?? {}).length,
    ...reportExtra,
  };
}

export function getTimeComparisonText(timeComparison: ComparisonDateRangeBookmarkFormat): null | Translatable {
  const comparisonDate = comparisonDateFromBookmarkFormat(timeComparison);
  if (comparisonDate) {
    return ({t, print}) =>
      t(`reports.date-range.comparison.compared-to`, {
        timePeriod: print(getTimeComparisonRangeText(comparisonDate)).toLocaleLowerCase(),
      });
  }
  return null;
}

export function getBoardFilterGroupPropTrackingProps(
  filters: Segmentation[] | PropertyFilter[],
  cards: CardOnDashboard[],
) {
  const groupPropertyFilters = filters.filter(isGroupPropertyFilter);
  const uniqueDataGroupIds = new Set<string>();
  groupPropertyFilters.forEach((propertyFilter) => {
    uniqueDataGroupIds.add(propertyFilter.dataGroupId);
  });

  const dataGroupNotSupportedCardResults = cards
    .filter((card) => Object.keys(card).includes(`bookmark`))
    .map((card) => checkBookmarkFilterOverridesSupported((card as DashboardCard<any>).bookmark, filters))
    .filter((check) => check.dataGroupNotSupported);

  return {
    'group property filter count': groupPropertyFilters.length,
    'unique data group id count in filters': uniqueDataGroupIds.size,
    'data group not supported card count': dataGroupNotSupportedCardResults.length,
    'data group id mismatch card count': dataGroupNotSupportedCardResults.filter(
      (check) => check.dataGroupNotSupportedReason === DataGroupNotSupportedReasonCode.DataGroupIdMisMatch,
    ).length,
    'user filter on data group card count': dataGroupNotSupportedCardResults.filter(
      (check) => check.dataGroupNotSupportedReason === DataGroupNotSupportedReasonCode.UserFilterOnDataGroup,
    ).length,
  };
}
